import React, { useMemo, useState } from "react";

/**
 * Generic bulk tag selector.
 *
 * Caller supplies the currently selected items and an `updateItem` callback
 * invoked once per modified item. Items are matched against tag values using
 * each item's `tags` array (`[{ id, name }, ...]`).
 *
 * Props:
 *   - all:           full list of available tag values (`[{ id, name }]`)
 *   - selected:      array of currently selected items
 *   - updateItem:    fn(updatedItem) — persist a single item with new tags
 *   - disabled:      whether the trigger button is disabled
 *   - buttonClassName / buttonLabel: trigger button styling
 */
const TagsList = ({
  all = [],
  disabled,
  selected = [],
  updateItem,
  buttonClassName = "uk-button hms-btn hms-white-btn",
  buttonLabel,
}) => {
  const [busy, setBusy] = useState(false);

  // Strict id-equality helper. Both ids must be defined to count as a match;
  // this prevents `null == undefined` style false positives that can make
  // unrelated tags appear selected ("select one, all become checked").
  const hasTag = (tags, tagValue) => {
    if (!Array.isArray(tags) || tagValue?.id == null) return false;
    return tags.some(
      (tag) => tag && tag.id != null && String(tag.id) === String(tagValue.id),
    );
  };

  const checkedAll = useMemo(() => {
    return all.map(
      (tagValue) =>
        selected.length > 0 &&
        selected.every((it) => hasTag(it.tags, tagValue)),
    );
  }, [selected, all]);

  const handleMultiTag = async (event, tagValue) => {
    if (busy) return;
    if (tagValue?.id == null) return;
    const checked = event.target.checked;
    // Snapshot `selected` so concurrent state updates from in-flight saves
    // don't change which items we iterate over mid-loop.
    const snapshot = selected.slice();
    let items;
    if (checked) {
      items = snapshot
        .filter((it) => !hasTag(it.tags, tagValue))
        .map((it) => ({
          ...it,
          tags: Array.isArray(it.tags) ? [...it.tags, tagValue] : [tagValue],
        }));
    } else {
      items = snapshot
        .filter((it) => hasTag(it.tags, tagValue))
        .map((it) => ({
          ...it,
          tags: (it.tags || []).filter(
            (tag) => !tag || String(tag.id) !== String(tagValue.id),
          ),
        }));
    }
    if (!updateItem || items.length === 0) return;
    setBusy(true);
    try {
      // Serialize updates so each PUT sees the latest tags state and avoids
      // overwriting tag changes from a previous in-flight request.
      for (const it of items) {
        await updateItem(it);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className={buttonClassName} disabled={disabled || busy}>
        <span uk-icon="icon: tag; ratio: 0.7"></span>
        {buttonLabel ? ` ${buttonLabel}` : null}
      </button>
      <div data-uk-dropdown="mode: click">
        <ul className="uk-nav uk-dropdown-nav tags-selector">
          {all.map((tagValue, index) => (
            <li key={tagValue?.id ?? `taglist_${index}`}>
              <label>
                <input
                  className="uk-checkbox"
                  type="checkbox"
                  checked={!!checkedAll[index]}
                  disabled={busy}
                  onChange={(event) => handleMultiTag(event, tagValue)}
                />
                {tagValue.name}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default TagsList;
